import Template from '../../../component/Template.js';
import { ToastContainer } from 'react-toastify';
import { memo, useEffect, useRef, useState } from 'react';
import { useUpload } from './hook/useUpload.js';
import { useGenerate } from './hook/useGenerate.js';

const MAROON = '#581f37';

const styles = {
    card: { borderRadius: 12, boxShadow: '0 2px 12px rgba(88,31,55,.1)' },
    title: { color: MAROON, fontWeight: 700, margin: 0 },
    fieldLabel: {
        position: 'absolute', top: 0, left: 10,
        fontSize: '0.7rem', fontWeight: 700, color: MAROON,
        backgroundColor: '#f8f9fa', padding: '0 5px',
        zIndex: 2, letterSpacing: '0.04em', lineHeight: 1, userSelect: 'none',
    },
    dateInput: {
        width: '100%', height: 40, border: '1px solid #ced4da', borderRadius: 6,
        padding: '0 12px', fontSize: '0.9rem', fontWeight: 500,
        color: '#212529', backgroundColor: 'white', outline: 'none',
        cursor: 'pointer', transition: 'border-color .15s, box-shadow .15s', boxSizing: 'border-box',
    },
    uploadZone: {
        width: '100%', minHeight: 80, border: '2px dashed #c4a0b0', borderRadius: 8,
        backgroundColor: '#fdf7f9', display: 'flex', alignItems: 'center',
        justifyContent: 'center', flexDirection: 'column', gap: 6, cursor: 'pointer',
        transition: 'border-color .2s, background .2s', padding: '12px 16px', boxSizing: 'border-box',
    },
    uploadZoneActive: { borderColor: MAROON, backgroundColor: '#f5e8ee' },
    uploadZoneDisabled: { opacity: 0.55, cursor: 'not-allowed' },
    fileChip: {
        display: 'inline-flex', alignItems: 'center', gap: 8,
        backgroundColor: '#f0e4ea', border: '1px solid #c4a0b0',
        borderRadius: 20, padding: '4px 12px', fontSize: '0.82rem', fontWeight: 600, color: MAROON,
    },
    removeBtn: {
        background: 'none', border: 'none', padding: 0,
        cursor: 'pointer', color: MAROON, lineHeight: 1, fontSize: '1rem', fontWeight: 700,
    },
    generateBtn: {
        backgroundColor: MAROON, color: '#fff', border: 'none', borderRadius: 8,
        padding: '10px 28px', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
        transition: 'background .2s', display: 'inline-flex', alignItems: 'center', gap: 8,
    },
    disabledBtn: { opacity: 0.55, cursor: 'not-allowed' },
};

const DL_CARDS = [
    {
        key: 'studentCount',
        label: 'Student Count',
        desc: 'Number of students per course per exam day',
        color: '#1a4a7a',
    },
    {
        key: 'conflictMatrix',
        label: 'Conflict Matrix',
        desc: 'All QP code pairs that share students, sorted by count',
        color: '#5b2d8e',
    },
];

const FORMAT_SECTIONS = [
    {
        label: 'Required Columns',
        color: '#1a3a5c',
        bg: '#eef2f7',
        items: [
            { col: 'A', name: 'college_name', desc: 'Name of the college / institution' },
            { col: 'B', name: 'student', desc: 'Student ID and name, e.g. MG24101A0023 - AROMAL P SHAJI' },
            { col: 'C', name: 'discipline', desc: 'Student\'s discipline, e.g. Biotechnology' },
            { col: 'D+', name: 'Course 1, Course 2 …', desc: 'One course per column, from column D onward (up to Course 9 or more)' },
        ],
    },
    {
        label: 'Course Cell Format',
        color: '#5c1a1a',
        bg: '#fdf2f2',
        formula: true,
        parts: [
            { segment: 'CourseGroup', example: 'Major', desc: 'One of: Major · Minor · VAC · SEC · Electives · AEC' },
            { segment: 'Type', example: 'B', desc: 'B (Board exam) or T (Theory)' },
            { segment: 'Discipline', example: 'Biotechnology', desc: 'Department / discipline name' },
            { segment: 'QPCode', example: '25901109', desc: 'Unique QP (question paper) code number' },
            { segment: 'CourseCode', example: 'MG4DSCBTG200', desc: 'Course code assigned to this student group' },
            { segment: 'CourseName', example: 'Molecular Biology', desc: 'Full name of the course' },
        ],
    },
    {
        label: 'Accepted Values',
        color: '#1a5c3a',
        bg: '#f0f9f4',
        items: [
            { col: 'Course Group', name: 'Major, Minor, VAC, SEC, Electives, AEC', desc: '' },
            { col: 'Type', name: 'B or T', desc: 'B = Both, T = Theory' },
            { col: 'Empty cells', name: 'Leave blank or write NULL', desc: 'Cells with no course for that student' },
            { col: 'File format', name: '.xlsx, .xls, or .csv', desc: 'All three are supported' },
        ],
    },
];

const FULL_EXAMPLE = 'Major - B - Biotechnology - 25901109 - MG4DSCBTG200 - Molecular Biology';

const Spinner = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="3" strokeLinecap="round" style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }}>
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83
                 M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
);
const DownloadIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
);
const UploadIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={MAROON}
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 16 12 12 8 16" />
        <line x1="12" y1="12" x2="12" y2="21" />
        <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
    </svg>
);
const ExcelIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="8" y1="13" x2="16" y2="13" />
        <line x1="8" y1="17" x2="16" y2="17" />
    </svg>
);
const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
const InfoIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="8.5" />
        <line x1="12" y1="12" x2="12" y2="16" />
    </svg>
);
const CloseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.5" strokeLinecap="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

function formatDisplayDate(val) {
    if (!val) return '';
    const d = new Date(val + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function FormatGuideModal({ onClose }) {
    const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

    return (
        <div
            onClick={handleBackdrop}
            style={{
                position: 'fixed', inset: 0, zIndex: 1050,
                backgroundColor: 'rgba(0,0,0,0.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '16px',
                animation: 'fadeIn .18s ease',
            }}
        >
            <div style={{
                backgroundColor: '#fff', borderRadius: 14,
                boxShadow: '0 20px 60px rgba(0,0,0,.25)',
                width: '100%', maxWidth: 720,
                maxHeight: '90vh', overflowY: 'auto',
                animation: 'slideUp .2s ease',
            }}>
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '18px 24px 16px',
                    borderBottom: '2px solid #f0e4ea',
                    position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1,
                    borderRadius: '14px 14px 0 0',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                            width: 34, height: 34, borderRadius: 8,
                            backgroundColor: '#f0e4ea',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: MAROON,
                        }}>
                            <ExcelIcon />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, color: MAROON, fontSize: '1rem', lineHeight: 1.2 }}>
                                Excel File Format Guide
                            </div>
                            <div style={{ fontSize: '0.72rem', color: '#888', marginTop: 2 }}>
                                How to prepare your student data file
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#888', padding: 4, borderRadius: 6,
                        display: 'flex', alignItems: 'center',
                        transition: 'color .15s',
                    }}
                        onMouseEnter={e => e.currentTarget.style.color = MAROON}
                        onMouseLeave={e => e.currentTarget.style.color = '#888'}
                    >
                        <CloseIcon />
                    </button>
                </div>

                <div style={{ padding: '20px 24px 24px' }}>

                    <SectionBlock section={FORMAT_SECTIONS[0]} />

                    <div style={{ marginTop: 20 }}>
                        <SectionTitle color={FORMAT_SECTIONS[1].color} label={FORMAT_SECTIONS[1].label} />
                        <div style={{
                            backgroundColor: FORMAT_SECTIONS[1].bg,
                            border: `1px solid ${FORMAT_SECTIONS[1].color}22`,
                            borderRadius: 10, padding: '14px 16px',
                        }}>
                            <p style={{ margin: '0 0 12px', fontSize: '0.82rem', color: '#444' }}>
                                Each course cell must follow this exact format, with each part separated by <code style={codeStyle}> - </code> (space-dash-space):
                            </p>

                            <div style={{
                                backgroundColor: '#fff', border: '1.5px solid #ddd',
                                borderRadius: 8, padding: '10px 14px',
                                fontFamily: 'monospace', fontSize: '0.82rem',
                                color: '#212529', wordBreak: 'break-all',
                                marginBottom: 14, lineHeight: 1.7,
                            }}>
                                {['CourseGroup', 'Type', 'Discipline', 'QPCode', 'CourseCode', 'CourseName'].map((part, i, arr) => (
                                    <span key={part}>
                                        <span style={{ fontWeight: 700, color: FORMAT_SECTIONS[1].color }}>{part}</span>
                                        {i < arr.length - 1 && <span style={{ color: '#aaa', margin: '0 2px' }}> - </span>}
                                    </span>
                                ))}
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {FORMAT_SECTIONS[1].parts.map((p, i) => (
                                    <div key={i} style={{
                                        display: 'grid', gridTemplateColumns: '110px 160px 1fr',
                                        gap: 10, alignItems: 'start',
                                        fontSize: '0.8rem',
                                    }}>
                                        <span style={{ fontWeight: 700, color: FORMAT_SECTIONS[1].color }}>{p.segment}</span>
                                        <code style={{ ...codeStyle, backgroundColor: '#fff3f3', color: '#c0392b' }}>{p.example}</code>
                                        <span style={{ color: '#555' }}>{p.desc}</span>
                                    </div>
                                ))}
                            </div>

                            <div style={{ marginTop: 14, borderTop: '1px dashed #ddd', paddingTop: 12 }}>
                                <div style={{ fontSize: '0.72rem', color: '#888', fontWeight: 600, marginBottom: 6, letterSpacing: '0.04em' }}>
                                    FULL EXAMPLE
                                </div>
                                <div style={{
                                    backgroundColor: '#fff', border: '1px solid #e8d5d5',
                                    borderRadius: 6, padding: '8px 12px',
                                    fontFamily: 'monospace', fontSize: '0.82rem',
                                    color: '#212529', wordBreak: 'break-all',
                                    display: 'flex', alignItems: 'center', gap: 8,
                                }}>
                                    <span style={{
                                        width: 8, height: 8, borderRadius: '50%',
                                        backgroundColor: '#1a7a4a', flexShrink: 0,
                                    }} />
                                    {FULL_EXAMPLE}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: 20 }}>
                        <SectionBlock section={FORMAT_SECTIONS[2]} />
                    </div>

                    <div style={{ marginTop: 20 }}>
                        <SectionTitle color='#333' label='Sample Excel Layout' />
                        <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #ddd' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem', minWidth: 560 }}>
                                <thead>
                                    <tr style={{ backgroundColor: MAROON }}>
                                        {['college_name', 'student', 'discipline', 'Course 1', 'Course 2', 'Course 3'].map(h => (
                                            <th key={h} style={{
                                                padding: '8px 10px', color: '#fff',
                                                fontWeight: 600, textAlign: 'left',
                                                whiteSpace: 'nowrap',
                                            }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {[
                                        ['St. Xavier\'s College', 'MG24101A0023 - AROMAL P SHAJI', 'Biotechnology',
                                            'Major - B - Biotechnology - 25901109 - MG4DSCBTG200 - Molecular Biology',
                                            'Major - B - Biotechnology - 25901110 - MG4DSCBTG201 - Immunology',
                                            'Minor - T - Chemistry - 25901201 - MG4DSCCHM200 - Organic Chemistry'],
                                        ['St. Xavier\'s College', 'MG24101A0024 - AFSAL K NASSAR', 'Biotechnology',
                                            'Major - B - Biotechnology - 25901109 - MG4DSCBTG200 - Molecular Biology',
                                            'VAC - T - General - 25901501 - MG4VACGEN200 - Environmental Studies',
                                            'SEC - T - Biotechnology - 25901301 - MG4SECBTG200 - Biostatistics'],
                                        ['City College', 'MG24102B0011 - ASHWIN M A', 'Computer Science',
                                            'Major - B - Computer Science - 25901189 - MG4DSCCSC200 - OOPs Concepts',
                                            'Minor - T - Mathematics - 25901250 - MG4DSCMAT200 - Linear Algebra',
                                            ''],
                                    ].map((row, ri) => (
                                        <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? '#fff' : '#fdf7f9' }}>
                                            {row.map((cell, ci) => (
                                                <td key={ci} style={{
                                                    padding: '7px 10px',
                                                    borderTop: '1px solid #eee',
                                                    color: ci >= 3 && cell ? '#1a3a5c' : '#444',
                                                    fontFamily: ci >= 3 ? 'monospace' : 'inherit',
                                                    fontSize: ci >= 3 ? '0.68rem' : '0.74rem',
                                                    maxWidth: ci >= 3 ? 200 : undefined,
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }}>
                                                    {cell || <span style={{ color: '#ccc', fontStyle: 'italic' }}>empty</span>}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p style={{ fontSize: '0.72rem', color: '#888', margin: '8px 0 0' }}>
                            Empty cells or cells containing <code style={codeStyle}>NULL</code> are ignored.
                        </p>
                    </div>

                    <div style={{
                        marginTop: 20, backgroundColor: '#fff8e8',
                        border: '1px solid #f0c040', borderRadius: 10, padding: '12px 16px',
                    }}>
                        <div style={{ fontWeight: 700, color: '#7a5a00', fontSize: '0.82rem', marginBottom: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
                            <span>⚠</span> Important Notes
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.8rem', color: '#555', lineHeight: 1.7 }}>
                            <li>The separator between each part is <strong> - </strong> (space, dash, space). Do not use just a dash.</li>
                            <li>Row 1 must be the header row with column names exactly as shown.</li>
                            <li>Each student can have multiple courses — add as many course columns as needed.</li>
                            <li>The QPCode must be consistent — the same course across different students must use the same QPCode.</li>
                            <li>Course names may contain hyphens; only the first 5 <code style={codeStyle}> - </code> delimiters are used to split the parts.</li>
                        </ul>
                    </div>

                </div>
            </div>
        </div>
    );
}

const codeStyle = {
    display: 'inline', fontFamily: 'monospace', fontSize: '0.78rem',
    backgroundColor: '#f3f3f3', border: '1px solid #e0e0e0',
    borderRadius: 4, padding: '1px 5px', color: '#212529',
};

function SectionTitle({ label, color }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            marginBottom: 10,
        }}>
            <div style={{ width: 4, height: 18, borderRadius: 2, backgroundColor: color }} />
            <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#212529' }}>{label}</span>
        </div>
    );
}

function SectionBlock({ section }) {
    return (
        <div>
            <SectionTitle color={section.color} label={section.label} />
            <div style={{
                backgroundColor: section.bg,
                border: `1px solid ${section.color}22`,
                borderRadius: 10, padding: '12px 16px',
                display: 'flex', flexDirection: 'column', gap: 8,
            }}>
                {section.items.map((item, i) => (
                    <div key={i} style={{
                        display: 'grid', gridTemplateColumns: '90px 1fr 1fr',
                        gap: 10, alignItems: 'start', fontSize: '0.8rem',
                    }}>
                        <span style={{
                            backgroundColor: section.color, color: '#fff',
                            borderRadius: 4, padding: '2px 7px',
                            fontWeight: 700, fontSize: '0.72rem',
                            display: 'inline-block', width: 'fit-content',
                        }}>{item.col}</span>
                        <code style={{ ...codeStyle, fontWeight: 600 }}>{item.name}</code>
                        <span style={{ color: '#555' }}>{item.desc}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Generate({ enabled = true }) {
    const [showGuide, setShowGuide] = useState(false);

    const {
        file, date, isDragging, fileInputRef,
        handleFileSelect, handleDrop, handleDragOver, handleDragLeave,
        handleRemoveFile, handleDateChange, openFilePicker,
    } = useUpload();

    const hook = useGenerate(file, date);
    const {
        isGenerating, generateError, handleGenerate,
        timetableUrl,
        isLoadingSC, scError, handleDownloadStudentCount,
        isLoadingCM, cmError, handleDownloadConflictMatrix,
    } = hook;

    const canGenerate = !!file && !!date && !isGenerating && enabled;

    useEffect(() => {
        const handler = (e) => {
            if (e.message?.includes('ResizeObserver loop')) e.stopImmediatePropagation();
        };
        window.addEventListener('error', handler);
        return () => window.removeEventListener('error', handler);
    }, []);

    useEffect(() => {
        if (!showGuide) return;
        const handler = (e) => { if (e.key === 'Escape') setShowGuide(false); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [showGuide]);

    const dateInputRef = useRef(null);
    const handleDateWrapperClick = () => {
        if (enabled && dateInputRef.current) dateInputRef.current.showPicker?.();
    };

    const cardMeta = {
        studentCount: { handler: handleDownloadStudentCount, loading: isLoadingSC, error: scError, done: hook.studentCountUrl },
        conflictMatrix: { handler: handleDownloadConflictMatrix, loading: isLoadingCM, error: cmError, done: hook.conflictMatrixUrl },
    };

    return (
        <>
            <ToastContainer />
            {showGuide && <FormatGuideModal onClose={() => setShowGuide(false)} />}

            <Template>
                <style>{`
                    @keyframes spin    { to { transform: rotate(360deg); } }
                    @keyframes slideIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
                    @keyframes fadeIn  { from { opacity:0; } to { opacity:1; } }
                    @keyframes slideUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
                    .date-field-input:focus { border-color:${MAROON}!important; box-shadow:0 0 0 0.2rem rgba(88,31,55,.2)!important; }
                    .upload-zone:hover:not(.disabled) { border-color:${MAROON}!important; background-color:#f5e8ee!important; }
                    .lazy-dl-btn { transition: filter .15s; }
                    .lazy-dl-btn:hover:not(:disabled) { filter: brightness(0.88); }
                    .lazy-dl-btn:disabled { opacity: 0.6; cursor: not-allowed; }
                    .guide-btn:hover { background-color: #f5e8ee!important; color: ${MAROON}!important; }
                `}</style>

                <div className="card" style={styles.card}>
                    <div className="col-md-12">

                        <div className="card-header" style={{ borderBottom: '2px solid #f0e4ea', borderRadius: '12px 12px 0 0' }}>
                            <div className="py-3 d-flex justify-content-between align-items-center">
                                <h5 style={styles.title}>Generate Time Table</h5>
                                <button
                                    className="guide-btn"
                                    onClick={() => setShowGuide(true)}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                        backgroundColor: '#fdf7f9',
                                        border: `1.5px solid #c4a0b0`,
                                        borderRadius: 8, padding: '6px 14px',
                                        fontSize: '0.8rem', fontWeight: 600,
                                        color: '#7a3a55', cursor: 'pointer',
                                        transition: 'background-color .15s, color .15s',
                                    }}
                                >
                                    <InfoIcon /> File Format Guide
                                </button>
                            </div>
                        </div>

                        <div className="card-body">
                            <div className="row">

                                <div className="col-md-6 col-12 mb-3">
                                    <div style={{ position: 'relative', width: '100%', paddingTop: 6 }}>
                                        <span style={styles.fieldLabel}>
                                            STUDENT EXCEL FILE <span style={{ color: '#dc3545' }}>*</span>
                                        </span>
                                        <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv"
                                            style={{ display: 'none' }} onChange={handleFileSelect} disabled={!enabled} />

                                        {!file ? (
                                            <div
                                                className={`upload-zone${!enabled ? ' disabled' : ''}`}
                                                style={{ ...styles.uploadZone, ...(isDragging ? styles.uploadZoneActive : {}), ...(!enabled ? styles.uploadZoneDisabled : {}), marginTop: 4 }}
                                                onClick={enabled ? openFilePicker : undefined}
                                                onDrop={enabled ? handleDrop : undefined}
                                                onDragOver={enabled ? handleDragOver : undefined}
                                                onDragLeave={enabled ? handleDragLeave : undefined}
                                            >
                                                <UploadIcon />
                                                <span style={{ color: MAROON, fontWeight: 600, fontSize: '0.88rem' }}>
                                                    Click to upload or drag & drop
                                                </span>
                                                <span style={{ color: '#888', fontSize: '0.78rem' }}>.xlsx, .xls, .csv supported</span>
                                                <span
                                                    style={{ color: '#a06080', fontSize: '0.73rem', marginTop: 2, cursor: 'pointer', textDecoration: 'underline' }}
                                                    onClick={e => { e.stopPropagation(); setShowGuide(true); }}
                                                >
                                                    View file format guide
                                                </span>
                                            </div>
                                        ) : (
                                            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={styles.fileChip}>
                                                    <ExcelIcon />
                                                    <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {file.name}
                                                    </span>
                                                    <button style={styles.removeBtn} onClick={handleRemoveFile} title="Remove">×</button>
                                                </div>
                                                <span style={{ color: '#888', fontSize: '0.75rem' }}>{(file.size / 1024).toFixed(1)} KB</span>
                                            </div>
                                        )}
                                        {!file && (
                                            <div style={{ color: '#dc3545', fontSize: '0.75rem', marginTop: 4, paddingLeft: 2 }}>
                                                Please upload the student data Excel file.
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-md-6 col-12 mb-3">
                                    <div style={{ position: 'relative', width: '100%', paddingTop: 6 }}>
                                        <span style={styles.fieldLabel}>
                                            EXAM START DATE <span style={{ color: '#dc3545' }}>*</span>
                                        </span>
                                        <div style={{ position: 'relative', marginTop: 4 }} onClick={handleDateWrapperClick}>
                                            <input
                                                ref={dateInputRef} type="date" className="date-field-input"
                                                value={date} disabled={!enabled}
                                                onChange={e => handleDateChange(e.target.value)}
                                                style={{ ...styles.dateInput, opacity: !enabled ? 0.55 : 1, cursor: !enabled ? 'not-allowed' : 'pointer', color: date ? '#212529' : '#adb5bd' }}
                                            />
                                        </div>
                                        {!date && (
                                            <div style={{ color: '#dc3545', fontSize: '0.75rem', marginTop: 4, paddingLeft: 2 }}>
                                                Please select the exam start date.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-12 mb-3 d-flex justify-content-center">
                                    <button
                                        style={{ ...styles.generateBtn, ...(!canGenerate ? styles.disabledBtn : {}) }}
                                        disabled={!canGenerate} onClick={handleGenerate}
                                        title={!file ? 'Upload file first' : !date ? 'Select date first' : 'Generate timetable'}
                                    >
                                        {isGenerating ? <><Spinner /> Generating...</> : 'Generate Timetable'}
                                    </button>
                                </div>
                            </div>

                            {generateError && (
                                <div className="row mt-2" style={{ animation: 'slideIn .3s ease' }}>
                                    <div className="col-12">
                                        <div className="alert alert-danger d-flex align-items-center gap-2 mb-0" role="alert">
                                            <span>&#9888;</span> {generateError}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {timetableUrl && (
                                <div className="row mt-3" style={{ animation: 'slideIn .4s ease' }}>
                                    <div className="col-12">
                                        <div style={{ backgroundColor: '#edf7f1', border: '1.5px solid #1a7a4a', borderRadius: 10, padding: '16px 20px' }}>
                                            <div style={{ fontWeight: 700, color: '#1a7a4a', fontSize: '0.95rem', marginBottom: 14 }}>
                                                ✅ Timetable Generated — Starting {formatDisplayDate(date)}
                                            </div>

                                            <div style={{ marginBottom: 16 }}>
                                                <a
                                                    href={timetableUrl.url}
                                                    download={timetableUrl.filename}
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 8,
                                                        backgroundColor: '#1a7a4a', color: '#fff', border: 'none',
                                                        borderRadius: 8, padding: '9px 22px', fontWeight: 600,
                                                        fontSize: '0.88rem', textDecoration: 'none', transition: 'filter .15s',
                                                    }}
                                                    onMouseEnter={e => e.currentTarget.style.filter = 'brightness(0.88)'}
                                                    onMouseLeave={e => e.currentTarget.style.filter = ''}
                                                >
                                                    <DownloadIcon /> Download Timetable
                                                </a>
                                            </div>

                                            <div style={{ borderTop: '1px solid #c3e6cb', marginBottom: 14 }} />

                                            <div style={{ fontSize: '0.78rem', color: '#555', marginBottom: 10, fontWeight: 600 }}>
                                                Additional Reports <span style={{ fontWeight: 400 }}>(click to generate &amp; download)</span>
                                            </div>

                                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                                {DL_CARDS.map(card => {
                                                    const meta = cardMeta[card.key];
                                                    const isDone = !!meta.done;
                                                    const isLoading = meta.loading;
                                                    return (
                                                        <div key={card.key} style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 210 }}>
                                                            <button
                                                                className="lazy-dl-btn"
                                                                disabled={isLoading}
                                                                onClick={meta.handler}
                                                                style={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                                                    backgroundColor: card.color, color: '#fff', border: 'none',
                                                                    borderRadius: 8, padding: '9px 18px', fontWeight: 600,
                                                                    fontSize: '0.88rem', cursor: 'pointer', textAlign: 'left',
                                                                }}
                                                            >
                                                                {isLoading ? <><Spinner /> Generating...</>
                                                                    : isDone ? <><CheckIcon /> {card.label} ↓</>
                                                                        : <><DownloadIcon /> {card.label}</>}
                                                            </button>
                                                            <span style={{ fontSize: '0.72rem', color: '#666', paddingLeft: 2 }}>{card.desc}</span>
                                                            {meta.error && (
                                                                <span style={{ fontSize: '0.72rem', color: '#dc3545', paddingLeft: 2 }}>⚠ {meta.error}</span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            </Template>
        </>
    );
}

export default memo(Generate);